import React, { useEffect } from "react";
import { HelmetProvider } from "react-helmet-async";
import {
	BrowserRouter,
	Routes,
	Route,
	Navigate,
	useLocation,
} from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { WorkoutProvider } from "./contexts/WorkoutContext";
import BrandHub from "./components/Landing/BrandHub";
import LandingPage from "./components/Landing/LandingPage";
import LocationPage from "./components/Landing/LocationPage";
import TrainerDashboard from "./components/Dashboard/TrainerDashboard";
import UserDashboard from "./components/Dashboard/UserDashboard";
import MerchOrderForm from "./components/Merch/MerchOrderForm";
import { getLocationBySlug } from "./config/locations";
import "bootstrap/dist/css/bootstrap.min.css";
import "./styles/App.css";

// Scroll the window to the top whenever the route (pathname) changes, so
// navigating from the hub to /sidcup or /ebbsfleet starts at the top of the
// page rather than keeping the previous scroll position.
function ScrollToTop() {
	const { pathname } = useLocation();
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);
	return null;
}

// Authenticated area: trainers and users get their dashboards. Logged-out
// visitors to "/" get the brand hub. This preserves the original auth/dashboard
// routing exactly — only the logged-out "/" view changed (LandingPage -> hub).
function AppContent() {
	const { user, isTrainer, isUser } = useAuth();

	if (!user) {
		return <BrandHub />;
	}

	if (isTrainer) {
		return <TrainerDashboard />;
	}

	if (isUser) {
		return <UserDashboard />;
	}

	// Fallback
	return <BrandHub />;
}

// Public per-location route. Resolves the slug to a config entry and renders
// the rich layout (LandingPage) for 'rich' locations or the generic
// LocationPage otherwise. Renders regardless of auth state (like /order).
function LocationRoute({ slug }) {
	const location = getLocationBySlug(slug);
	if (!location) {
		return <Navigate to="/" replace />;
	}
	if (location.layout === "rich") {
		return <LandingPage location={location} />;
	}
	return <LocationPage location={location} />;
}

function App() {
	return (
		<HelmetProvider>
			<BrowserRouter>
				<AuthProvider>
					<WorkoutProvider>
						<div className="App">
							<ScrollToTop />
							<Routes>
								<Route path="/order" element={<MerchOrderForm />} />
								<Route
									path="/sidcup"
									element={<LocationRoute slug="sidcup" />}
								/>
								<Route
									path="/ebbsfleet"
									element={<LocationRoute slug="ebbsfleet" />}
								/>
								<Route path="*" element={<AppContent />} />
							</Routes>
						</div>
					</WorkoutProvider>
				</AuthProvider>
			</BrowserRouter>
		</HelmetProvider>
	);
}

export default App;
